import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'

import App from './App'
import { ApiError } from './api/client'
import { initAuthBridge, useAuthStore } from './store/auth'
import { ErrorBoundary, initErrorReporting } from './lib/errorReporting'
import './styles/index.css'

// Connect the HTTP client to the auth store before any request can fire.
initAuthBridge()
// Uncaught errors are reported to the API from here on.
initErrorReporting()

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      // The role was taken away while signed in: nothing here will load again.
      if (error instanceof ApiError && error.code === 'admin_required') useAuthStore.getState().clear()
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Retrying a 4xx just repeats the same rejection.
        if (error instanceof ApiError && !error.isRetryable) return false
        return failureCount < 2
      },
    },
    mutations: { retry: false },
  },
})

const container = document.getElementById('root')
if (!container) throw new Error('Root element #root is missing from index.html')

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
)
